import { useState } from 'react'

import { ShiftManagementPublishScheduleDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useNotifications } from 'modules/shift_management/hooks/useNotifications'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'

const usePublishShifts = () => {
  const [showPublishModal, togglePublishModal] = useState<boolean>(false)
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ sendNotifications }] = useNotifications()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [{ withoutShiftIds, teamIds, employeeIds, customFilters, locationIds }] = useFilters()
  const [{ canManageShifts }] = usePermissions()

  const [publishSchedule, { loading }] = useMutation(ShiftManagementPublishScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.publishShiftmanagementSchedule,
    update: (cache, { data }) => {
      const schedule = data?.shiftManagementMutations.publishShiftmanagementSchedule.schedule
      const shifts = schedule?.shifts || []
      const restDays = schedule?.restDays

      cache.batch({
        update: (batchedCache) => {
          shifts.forEach((shift) => {
            const cacheId = batchedCache.identify({
              __typename: 'ShiftManagementShift',
              id: shift.id,
            })
            if (!cacheId) return
            batchedCache.modify({
              id: cacheId,
              fields: {
                state: (currentState) =>
                  currentState === shift.state ? currentState : shift.state,
              },
            })
          })

          restDays?.forEach((restDay) => {
            const cacheId = batchedCache.identify({
              __typename: 'ShiftManagementRestDay',
              id: restDay.id,
            })
            if (!cacheId) return
            batchedCache.modify({
              id: cacheId,
              fields: {
                state: (currentState) =>
                  currentState === restDay.state ? currentState : restDay.state,
              },
            })
          })
        },
      })
    },
  })

  const onConfirm = async () => {
    const res = await publishSchedule({
      variables: {
        customFilters: customFilters,
        employeeIds: employeeIds,
        endAt: view.endAt,
        locationIds: locationIds,
        sendNotifications: sendNotifications,
        startAt: view.startAt,
        teamIds: teamIds,
        withoutShiftIds: withoutShiftIds,
        logicOperator: LOGIC_OPERATOR.AND,
        viewType: view.viewType,
      },
    })

    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.publish_success'),
      })
      let automaticShiftsRowsCount = 0
      shiftsRows.forEach((row) => {
        row.shifts?.shifts?.forEach((shift) => {
          if (shift.creationMode === 'automatic') {
            automaticShiftsRowsCount++
          }
        })
      })

      const numberOfFilters =
        customFilters.length +
        (teamIds?.length ?? 0) +
        (employeeIds?.length ?? 0) +
        locationIds.length

      const shiftsData = handled.data?.schedule?.shifts || []
      const restDaysData = handled.data?.schedule?.restDays || []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || null,
        })
      )
      const newRestDays = restDaysData.flatMap((restDay) => {
        const employee = restDay.employee
        if (!employee) return []

        return RestDay.build({
          id: restDay.id,
          employee_id: employee.id,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: employee.fullName || '',
          employeeAvatar: employee.avatar?.url || '',
        })
      })
      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: newShifts, view })
      dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })
      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays: newRestDays })
      dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays: newRestDays })

      dispatchSchedule({ type: 'PUBLISH_WEEK' })

      track(trackingEvents.shiftManagement.publishShiftManagementSubmit, {
        view: view.viewType,
        view_type_by: view.groupType,
        from_date: view.startAt,
        to_date: view.endAt,
        number_of_filters: numberOfFilters,
        send_notification: sendNotifications,
        automatic_shifts_count: automaticShiftsRowsCount,
        shift_ids: shiftsRows.flatMap((row) => row.shifts?.shifts?.map((shift) => shift.id) || []),
      })

      togglePublishModal(false)
    }
  }

  return {
    submitting: loading,
    showPublishModal: showPublishModal && canManageShifts,
    togglePublishModal,
    onConfirm,
    showPublishButton: canManageShifts,
  }
}

export default usePublishShifts
